import { smartBillDraftsApi } from '../services/smartBillApi';

const DRAFTS_KEY = 'locsy_admin_smart_bill_drafts_v1';
const MAX_DRAFTS = 20;

export const SMART_BILL_DRAFT_HANDOFF_KEY = 'smartBillDraftData';

export const readSmartBillDrafts = () => {
  try {
    const raw = localStorage.getItem(DRAFTS_KEY);
    const rows = raw ? JSON.parse(raw) : [];
    return Array.isArray(rows) ? rows : [];
  } catch (error) {
    console.error('Unable to read smart bill drafts', error);
    return [];
  }
};

export const writeSmartBillDrafts = (drafts) => {
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
};

const cacheDraftLocally = (draft) => {
  if (!draft?.id) return draft;
  const drafts = readSmartBillDrafts().filter((row) => row.id !== draft.id);
  const next = [draft, ...drafts].slice(0, MAX_DRAFTS);
  writeSmartBillDrafts(next);
  return draft;
};

const removeDraftLocally = (draftId) => {
  if (!draftId) return readSmartBillDrafts();
  const nextDrafts = readSmartBillDrafts().filter((draft) => draft.id !== draftId);
  writeSmartBillDrafts(nextDrafts);
  return nextDrafts;
};

/**
 * Create a new draft, or update an existing one when draftId is provided.
 * Writes localStorage immediately, then syncs to the shared API (React + Mitra).
 */
export const saveSmartBillDraft = (payload, draftId = null) => {
  const drafts = readSmartBillDrafts();
  const existingId = draftId || payload?.id || null;

  let draft;
  if (existingId) {
    const index = drafts.findIndex((row) => row.id === existingId);
    if (index >= 0) {
      draft = {
        ...drafts[index],
        ...payload,
        id: existingId,
        source: payload?.source || drafts[index].source || 'react',
        savedAt: new Date().toISOString(),
      };
      drafts.splice(index, 1);
      writeSmartBillDrafts([draft, ...drafts].slice(0, MAX_DRAFTS));
    }
  }

  if (!draft) {
    draft = {
      id: existingId || `draft-${Date.now()}`,
      savedAt: new Date().toISOString(),
      source: 'react',
      ...payload,
    };
    draft.id = existingId || draft.id;
    draft.source = draft.source || 'react';
    writeSmartBillDrafts([draft, ...drafts].slice(0, MAX_DRAFTS));
  }

  // Fire-and-forget server sync so Mitra can retrieve the same draft.
  void syncSmartBillDraftToServer(draft).catch((error) => {
    console.warn('Smart bill draft server sync failed', error);
  });

  return draft;
};

export const syncSmartBillDraftToServer = async (draft) => {
  if (!draft?.id) return null;
  const response = await smartBillDraftsApi.save({
    id: draft.id,
    source: draft.source || 'react',
    label: draft.label,
    totalAmount: draft.totalAmount,
    customerData: draft.customerData,
    selectedServices: draft.selectedServices,
    paymentData: draft.paymentData,
    couponBillItems: draft.couponBillItems,
    payload: {
      customerData: draft.customerData || {},
      selectedServices: Array.isArray(draft.selectedServices)
        ? draft.selectedServices
        : [],
      paymentData: draft.paymentData || {},
      couponBillItems: Array.isArray(draft.couponBillItems)
        ? draft.couponBillItems
        : [],
      totalAmount: draft.totalAmount,
      label: draft.label,
    },
  });
  const saved = response?.data || response;
  if (saved?.id) {
    cacheDraftLocally({
      ...draft,
      ...saved,
      savedAt: saved.savedAt || draft.savedAt,
    });
  }
  return saved;
};

export const fetchSmartBillDraftsFromServer = async () => {
  const localRows = readSmartBillDrafts();
  try {
    const response = await smartBillDraftsApi.getAll();
    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response)
        ? response
        : [];

    // Prefer server (shared with Mitra). Keep local-only drafts that are not synced yet.
    const byId = new Map();
    rows.forEach((row) => {
      if (row?.id) byId.set(row.id, row);
    });
    localRows.forEach((row) => {
      if (row?.id && !byId.has(row.id)) {
        byId.set(row.id, row);
        // Best-effort upload of older browser-only drafts.
        void syncSmartBillDraftToServer(row).catch(() => {});
      }
    });

    const merged = Array.from(byId.values()).sort((a, b) =>
      String(b.savedAt || '').localeCompare(String(a.savedAt || '')),
    );
    writeSmartBillDrafts(merged.slice(0, MAX_DRAFTS));
    return merged;
  } catch (error) {
    console.warn('Unable to fetch smart bill drafts from server', error);
    return localRows;
  }
};

export const getSmartBillDraft = (draftId) =>
  readSmartBillDrafts().find((draft) => draft.id === draftId) || null;

export const deleteSmartBillDraft = (draftId) => {
  const next = removeDraftLocally(draftId);
  if (draftId) {
    void smartBillDraftsApi.remove(draftId).catch((error) => {
      console.warn('Smart bill draft server delete failed', error);
    });
  }
  return next;
};

export const deleteSmartBillDraftAsync = async (draftId) => {
  const next = removeDraftLocally(draftId);
  if (draftId) {
    try {
      await smartBillDraftsApi.remove(draftId);
    } catch (error) {
      console.warn('Smart bill draft server delete failed', error);
    }
  }
  return next;
};

export const clearSmartBillDraftHandoff = () => {
  sessionStorage.removeItem(SMART_BILL_DRAFT_HANDOFF_KEY);
};
